import React, { useEffect, useState } from "react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import ko from "date-fns/locale/ko"
import styled, { css } from "styled-components"
import {
  Absolute,
  COLOR_SYSTEM,
  Flex,
  SHADOW,
  Text,
  ToggleButton,
} from "components/atoms"
import Dropdown, { DropdownMenuItem, DropdownMenuItems } from "../Dropdown"

export const DatePickerButton = React.forwardRef<HTMLDivElement, any>(
  ({ value, disabled, isOpen, onToggle }, ref) => (
    <div ref={ref} onMouseDown={onToggle}>
      <ToggleButton
        color="Gray"
        size="Small"
        type="IconWithText"
        variant="Ghost"
        active={!disabled}
        icon={isOpen ? "Caret Up Filled" : "Caret Down Filled"}
        right
        onClick={() => {}}
        disabled={disabled}
        style={{
          color: COLOR_SYSTEM.get("Gray")[700],
          fontSize: "13px",
          padding: "0px 10px",
          width: "150px",
          height: "36px",
          borderRadius: "8px",
          backgroundColor: COLOR_SYSTEM.get("Gray")[20],
          border: "none",
        }}
      >
        {value}
      </ToggleButton>
    </div>
  )
)

interface DateTimePickerStyledProps {
  disabled: boolean
}

const disableDate = () => {
  let styles = ""

  const today = new Date()
  const date = today.getDate()

  for (let i = 1; i < date; i++) {
    let day = ""
    String(i).length === 1
      ? (day = `0${i}`)
      : String(i).length === 2
      ? (day = `${i}`)
      : (day = String(i))
    styles += `
      .react-datepicker__day--0${day}{
        color: ${COLOR_SYSTEM.get("Gray")?.[400]};
        pointer-events: none;
    }
    `
  }
  return css`
    ${styles}
  `
}

const disableTime = () => {
  const today = new Date()
  const date = today.getHours()
}

const DateTimePickerLayout = styled.div<DateTimePickerStyledProps>`
  display: flex;
  align-items: center;
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
  z-index: 9999;

  .right-button__icon {
    margin-left: 0px;
  }

  .datepicker__range-text {
    margin: 0 0px;
  }

  .react-datepicker {
    width: 240px;
    padding: 8px;
    border: none;
    border-radius: 8px;
    ${SHADOW.get("4")};
    background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  }

  .react-datepicker__month-container {
    width: 100%;
  }

  .react-datepicker__header {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
    border-bottom: none;
    margin-top: 24px;
    padding: 0;
  }

  .react-datepicker__navigation {
    width: 20px;
    height: 20px;
    top: 31px;
    color: white;
  }

  .react-datepicker__navigation-icon {
    visibility: hidden;
  }

  .react-datepicker__navigation--previous {
    left: 24px;
    visibility: hidden;
  }

  .react-datepicker__navigation--next {
    right: 24px;
    visibility: hidden;
  }

  .react-datepicker__current-month {
    font-size: 13px;
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    margin-bottom: 12px;
  }

  .react-datepicker__day--${new Date().getDay() + 1} {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  }

  .react-datepicker__day-name {
    height: 32px;
    width: 32px;
    font-style: normal;
    font-weight: 500;
    line-height: 32px;
    font-size: 13px;
    padding: 0;
    margin: 0;
    border-radius: 0;
    color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  }

  .react-datepicker__day-names {
    justify-content: space-between;
    line-height: 32px;
    font-size: 13px;
    margin: 0;
    padding: 0;
    height: 32px;
    color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  }

  .react-datepicker__month {
    margin: 0.4rem 0;
  }

  .react-datepicker__week {
    display: flex;
  }

  .react-datepicker__day {
    height: 32px;
    line-height: 32px;
    flex: 1 1 33%;
    font-size: 13px;
    color: ${COLOR_SYSTEM.get("Gray")?.[900]};
    margin: 0;
    border-radius: 0;
  }

  .react-datepicker__day:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[15]};
  }

  .react-datepicker__day--disabled {
    color: #ccc;
  }

  .react-datepicker__day--in-range {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[15]};
  }

  .react-datepicker__day--in-selecting-range {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[15]};
    border-radius: 0;
  }

  .react-datepicker__day--in-selecting-range:not(
      .react-datepicker__day--in-range
    ) {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[15]};
    border-radius: 0;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--wekend,
  .react-datepicker__day--keyboard-selected,
  .react-datepicker__day--range-start {
    color: ${COLOR_SYSTEM.get("Gray")?.[0]};
    background-color: ${COLOR_SYSTEM.get("Skyblue")?.[400]};
    border-radius: 50%;
  }

  .react-datepicker__input-container {
    display: inline-block;
  }

  .react-datepicker__day--outside-month {
    color: ${COLOR_SYSTEM.get("Gray")?.[400]};
    visibility: hidden;
  }

  ${disableDate()}
`

const today = new Date()
const todayDate = today.getDate()

export type DateKind = "start" | "end"
interface DateTimePickerProps {
  reservedDate: Date | string
  disabled: boolean
  error?: boolean
  onChangeDate: (name: DateKind, date: Date) => void
  onClickMenu: () => void
  showMenu: boolean
}

const DateTiemPicker: React.FC<DateTimePickerProps> = ({
  reservedDate,
  disabled,
  error,
  onChangeDate,
}) => {
  const dayOfWeek = new Date(reservedDate).getDay()
  const timeOfTheDay = new Date(reservedDate).getHours()

  const [showCalendar, setShowCalendar] = useState<boolean>(false)
  const [showMenu, setShowMenu] = useState<boolean>(false)
  const [selectedTime, setSelectedTime] = useState<any>()

  const dayOfWeekStr = ["일", "월", "화", "수", "목", "금", "토"][dayOfWeek]
  const timeOptions = [
    "오전 12시",
    "오전 1시",
    "오전 2시",
    "오전 3시",
    "오전 4시",
    "오전 5시",
    "오전 6시",
    "오전 7시",
    "오전 8시",
    "오전 9시",
    "오전 10시",
    "오전 11시",
    "오후 12시",
    "오후 1시",
    "오후 2시",
    "오후 3시",
    "오후 4시",
    "오후 5시",
    "오후 6시",
    "오후 7시",
    "오후 8시",
    "오후 9시",
    "오후 10시",
    "오후 11시",
  ]



  const onClickMenu = () => {
    setShowMenu(!showMenu)
  }

  return (
    <DateTimePickerLayout disabled={disabled}>
      <DatePicker
        className="datepicker-start"
        locale={ko}
        dateFormat={`yyyy-MM-dd (${dayOfWeekStr})`}
        dateFormatCalendar={"yyyy년 MM월"}
        selected={new Date(reservedDate)}
        onChange={(date: Date) => {
          setShowCalendar(false)
          onChangeDate("start", date)
        }}
        open={showCalendar}
        onClickOutside={() => setShowCalendar(false)}
        selectsStart
        showPopperArrow={true}
        disabled={disabled}
        placeholderText="예약일"
        customInput={
          <DatePickerButton
            disabled={disabled}
            isOpen={showCalendar}
            onToggle={() => setShowCalendar((prev) => !prev)}
          />
        }
      />
      <Dropdown
        defaultValue={timeOptions[timeOfTheDay]}
        selected={selectedTime}
        size="Small"
        buttonCustomStyle={{
          color: COLOR_SYSTEM.get("Gray")[700],
          fontSize: "13px",
          marginLeft: "2px",
          padding: "0px 10px",
          width: "92px",
          height: "36px",
          borderRadius: "8px",
          backgroundColor: COLOR_SYSTEM.get("Gray")[20],
          border: "none",
        }}
        onClick={() => {
          onClickMenu()
        }}
        active={showMenu}
        customStyle={{
          height: "300px",
          width: "92px",
          borderRadius: "8px",
          padding: "0px",
          margin: "0px",
          scrollBehavior: "auto",
        }}
      >
        <DropdownMenuItems>
          {timeOptions.map((timeOption, index) => (
            <>
              <DropdownMenuItem
                key={timeOption}
                onClick={(e) => {
                  e.stopPropagation()
                  if (
                    new Date().getDate() == new Date(reservedDate).getDate() &&
                    new Date().getHours() >= index 
                  )
                    return
                  setShowMenu(false)
                  const newReservedDate = new Date(reservedDate)
                  newReservedDate.setHours(index)
                  newReservedDate.setMinutes(0)
                  newReservedDate.setSeconds(0)
                  setSelectedTime(timeOption)
                  if(newReservedDate.getMonth() !== new Date().getMonth()) return
                  onChangeDate("start", new Date(newReservedDate))
                }}
              >
                {new Date().getDate() !== new Date(reservedDate).getDate() ? (
                  <Text
                    textType={"InputMedium"}
                    color={COLOR_SYSTEM.get("Gray")[700]}
                    style={{ whiteSpace: "nowrap", fontSize: "13px" }}
                  >
                    {timeOption}
                  </Text>
                ) : new Date().getHours() >= index ? (
                  <Text
                    textType={"InputMedium"}
                    color={COLOR_SYSTEM.get("Gray")[300]}
                    style={{
                      whiteSpace: "nowrap",
                      fontSize: "13px",
                      pointerEvents: "none",
                    }}
                  >
                    {timeOption}
                  </Text>
                ) : (
                  <Text
                    textType={"InputMedium"}
                    color={COLOR_SYSTEM.get("Gray")[700]}
                    style={{ whiteSpace: "nowrap", fontSize: "13px" }}
                  >
                    {timeOption}
                  </Text>
                )}
              </DropdownMenuItem>
            </>
          ))}
        </DropdownMenuItems>
      </Dropdown>
    </DateTimePickerLayout>
  )
}

export default DateTiemPicker
